import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Eye, Heart, BarChart3, Calendar as CalendarIcon, MessageCircle, Star, Reply, ExternalLink, Banknote, UserCircle } from 'lucide-react';
import { format, parse, isValid } from 'date-fns';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Button } from '../components/ui/button';
import { cn } from '@/lib/utils';
import axios from 'axios';

/**
 * MyBooks.jsx
 * Author / Admin dashboard for "books I wrote" — shows the author's
 * private library of assigned books plus a per-book engagement deep-dive
 * (calendar heatmap + completion-% line chart + summary stats).
 *
 * Routes:
 *   /my-books  — guarded for role in { 'author', 'admin' }
 *
 * Endpoints used:
 *   GET /api/my-books
 *   GET /api/my-books/{book_id}/engagement?from=YYYY-MM-DD&to=YYYY-MM-DD
 *
 * Authors NEVER see royalty data on this page — that's admin-only and lives
 * in AdminSettings → Royalty Report.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const card = 'bg-[#1a1d2e] rounded-xl border border-white/10 p-5';
const badge = (color) => `inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${color}`;

const todayISO = () => new Date().toISOString().split('T')[0];
const isoNDaysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().split('T')[0];

const authHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const MyBooks = () => {
  const [books, setBooks] = useState([]);
  const [meta, setMeta] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [engLoading, setEngLoading] = useState(false);
  const [from, setFrom] = useState(isoNDaysAgo(30));
  const [to, setTo] = useState(todayISO());
  // Discussion + Reviews feed for the selected book (lazy-fetched)
  const [feed, setFeed] = useState(null);
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedTab, setFeedTab] = useState('discussion'); // 'discussion' | 'reviews'
  // Bank-details setup banner — flips to false once user fills in payable fields
  const [bankStatus, setBankStatus] = useState({ has_details: true, loaded: false });
  // Public author page name — powers the "View My Public Profile" shortcut
  const [publicProfileName, setPublicProfileName] = useState(null);

  useEffect(() => {
    if (localStorage.getItem('userRole') !== 'author') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/authors/me/profile`, { headers: authHeader() });
        if (!cancelled && res.data?.author_name) setPublicProfileName(res.data.author_name);
      } catch { /* no linked books yet — hide the shortcut */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/me/payout-bank-details/status`, { headers: authHeader() });
        if (!cancelled) setBankStatus({ has_details: !!res.data?.has_details, loaded: true });
      } catch {
        if (!cancelled) setBankStatus({ has_details: true, loaded: true }); // fail silent — don't nag on errors
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch list of my books
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/my-books`, { headers: authHeader() });
        if (cancelled) return;
        setBooks(res.data?.books || []);
        setMeta(res.data?.meta || null);
      } catch {
        if (!cancelled) setBooks([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch engagement whenever the selected book or date range changes
  useEffect(() => {
    if (!selectedBook) return undefined;
    let cancelled = false;
    (async () => {
      setEngLoading(true);
      try {
        const res = await axios.get(
          `${API}/my-books/${selectedBook.id}/engagement?from=${from}&to=${to}`,
          { headers: authHeader() }
        );
        if (!cancelled) setEngagement(res.data);
      } catch {
        if (!cancelled) setEngagement(null);
      } finally {
        if (!cancelled) setEngLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedBook, from, to]);

  // Fetch discussion + reviews feed when book changes (no date dep).
  useEffect(() => {
    if (!selectedBook) return undefined;
    let cancelled = false;
    (async () => {
      setFeedLoading(true);
      try {
        const res = await axios.get(
          `${API}/my-books/${selectedBook.id}/feed?chat_limit=50&reviews_limit=20`,
          { headers: authHeader() }
        );
        if (!cancelled) setFeed(res.data);
      } catch {
        if (!cancelled) setFeed(null);
      } finally {
        if (!cancelled) setFeedLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedBook]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-[#0d0f1c] text-white p-6 space-y-6" data-testid="my-books-page">
      {/* Top nav */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link
          to="/welcome"
          className="inline-flex items-center text-white/60 hover:text-white text-sm"
          data-testid="my-books-back"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Library
        </Link>
        {meta?.role === 'admin' && (
          <span className={badge('bg-fuchsia-500/15 text-fuchsia-300')}>Admin view · all books</span>
        )}
        {publicProfileName && (
          <Link
            to={`/author/${encodeURIComponent(publicProfileName)}`}
            className="inline-flex items-center gap-1.5 text-sm font-medium bg-red-600 hover:bg-red-500 text-white rounded-full px-4 py-2 transition-colors"
            data-testid="view-public-profile-btn"
          >
            <UserCircle className="h-4 w-4" /> View My Public Profile
          </Link>
        )}
      </div>

      <div>
        <h1 className="text-2xl font-semibold text-white">My Books</h1>
        <p className="text-sm text-white/40 mt-1">
          {meta?.role === 'admin'
            ? `Showing all ${books.length} books in the catalogue. Use any row to drill into engagement.`
            : 'Books assigned to your author account. Engagement starts from the date the book was added to your dashboard.'}
        </p>
      </div>

      {/* Missing-bank-details banner — only show for authors who haven't
          completed their payout details. Admins almost never need this. */}
      {bankStatus.loaded && !bankStatus.has_details && meta?.role !== 'admin' && (
        <div
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3"
          data-testid="my-books-bank-banner"
        >
          <div className="flex items-start gap-3">
            <Banknote className="h-5 w-5 text-amber-300 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-200">
                Set up your payout bank details
              </p>
              <p className="text-xs text-amber-100/80 mt-0.5">
                We need your bank info to send your quarterly royalty payments. Takes 1 minute.
              </p>
            </div>
          </div>
          <Link
            to="/me/payout-bank-details"
            className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold bg-amber-400 text-amber-950 hover:bg-amber-300"
            data-testid="my-books-bank-banner-cta"
          >
            Add bank details
          </Link>
        </div>
      )}

      {/* Book Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {books.length === 0 && (
          <p className="text-white/40 col-span-full text-sm" data-testid="my-books-empty">
            No books assigned to your account yet. Contact admin to get linked to your work.
          </p>
        )}
        {books.map((book) => (
          <button
            key={book.id}
            onClick={() => { setSelectedBook(book); setEngagement(null); setFeed(null); }}
            className={`text-left rounded-xl border transition-all overflow-hidden ${
              selectedBook?.id === book.id
                ? 'border-red-500/60 bg-red-500/10'
                : 'border-white/10 bg-[#1a1d2e] hover:border-white/25'
            }`}
            data-testid={`my-books-card-${book.id}`}
          >
            {book.cover_image_url ? (
              <img
                src={book.cover_image_url}
                alt={book.title}
                className="w-full h-36 object-cover"
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&q=80';
                }}
              />
            ) : (
              <div className="w-full h-36 bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center text-white/40 text-xs">
                No cover
              </div>
            )}
            <div className="p-3">
              <p className="text-sm font-medium text-white line-clamp-2">{book.title}</p>
              {book.author && (
                <p className="text-xs text-white/50 mt-0.5 truncate">by {book.author}</p>
              )}
              <p className="text-xs text-white/40 mt-1">
                Added {new Date(book.assigned_at || book.created_at).toLocaleDateString()}
              </p>
              {book.auto_assigned && (
                <span className={`mt-2 ${badge('bg-teal-500/15 text-teal-400')}`}>Auto-assigned</span>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Engagement Panel */}
      {selectedBook && (
        <div className="space-y-4" data-testid="engagement-panel">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="text-lg font-medium text-white">
              {selectedBook.title} — Engagement
            </h2>
            <div className="flex items-center gap-2 text-sm">
              <DateRangePicker
                label="From"
                value={from}
                max={to}
                onChange={setFrom}
                testId="engagement-from"
              />
              <DateRangePicker
                label="To"
                value={to}
                min={from}
                max={todayISO()}
                onChange={setTo}
                testId="engagement-to"
              />
            </div>
          </div>

          {engLoading && <LoadingSpinner />}

          {engagement && !engLoading && (
            <>
              <div className="grid grid-cols-3 gap-4">
                <StatCard
                  label="Total Views"
                  value={Number(engagement.summary.total_views).toLocaleString()}
                  Icon={Eye}
                  testId="stat-total-views"
                />
                <StatCard
                  label="Engagement"
                  value={`${Number(engagement.summary.avg_engagement_score || 0).toFixed(1)}%`}
                  Icon={Heart}
                  testId="stat-engagement"
                />
                <StatCard
                  label="Avg. Completion"
                  value={`${Number(engagement.summary.avg_completion_pct).toFixed(1)}%`}
                  Icon={BarChart3}
                  testId="stat-avg-completion"
                />
              </div>

              <div className={card} data-testid="engagement-heatmap">
                <h3 className="text-sm font-medium text-white/70 mb-4">Daily View Count Calendar</h3>
                <CalendarHeatMap data={engagement.daily} from={from} to={to} />
              </div>

              <div className={card} data-testid="engagement-completion-chart">
                <h3 className="text-sm font-medium text-white/70 mb-4">Completion % Over Time</h3>
                <SimpleLineChart
                  data={engagement.daily}
                  xKey="stat_date"
                  yKey="completion_pct"
                  color="#ef4444"
                  maxY={100}
                  unit="%"
                />
              </div>
            </>
          )}

          {/* Discussion + Reviews — read-only with "Reply" deep-link to chat */}
          <BookActivityPanel
            book={selectedBook}
            feed={feed}
            loading={feedLoading}
            tab={feedTab}
            onTabChange={setFeedTab}
          />
        </div>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────
const formatRelative = (ts) => {
  if (!ts) return '';
  const date = new Date(ts);
  const diffMs = Date.now() - date.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 604800) return `${Math.floor(sec / 86400)}d ago`;
  return date.toLocaleDateString();
};

const BookActivityPanel = ({ book, feed, loading, tab, onTabChange }) => {
  // Always render the card so the section is always present — even on
  // empty / loading states. This is the most engaging part of the dashboard
  // (authors love seeing real readers talking about their book), so we
  // keep it visible even when there's nothing to show yet.
  const chat = feed?.chat || [];
  const reviews = feed?.reviews || [];
  // Newest-first comes back from the API; reverse the chat so it reads
  // chronologically (top-to-bottom oldest-to-newest) like a real chat UI.
  const chatChrono = [...chat].reverse();

  const handleReply = (sectionAnchor) => {
    // Deep-link to the public book page where authors can post a real reply.
    // Using a new tab so the dashboard state is preserved on return.
    if (!book?.id) return;
    window.open(`/book/${book.id}${sectionAnchor ? `#${sectionAnchor}` : ''}`, '_blank');
  };

  return (
    <div className={card} data-testid="book-activity-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-sm font-medium text-white/70 flex items-center gap-2">
          {tab === 'discussion' ? <MessageCircle className="h-4 w-4 text-cyan-400" /> : <Star className="h-4 w-4 text-yellow-400" />}
          {tab === 'discussion' ? 'Recent Discussion' : 'Recent Reviews'}
        </h3>
        <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1" role="tablist">
          <button
            role="tab"
            aria-selected={tab === 'discussion'}
            onClick={() => onTabChange('discussion')}
            className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
              tab === 'discussion'
                ? 'bg-cyan-500/25 text-cyan-200'
                : 'text-white/40 hover:text-white/70'
            }`}
            data-testid="feed-tab-discussion"
          >
            <MessageCircle className="h-3.5 w-3.5 inline mr-1" />
            Discussion {chat.length > 0 && <span className="ml-1 text-[10px] opacity-70">({chat.length})</span>}
          </button>
          <button
            role="tab"
            aria-selected={tab === 'reviews'}
            onClick={() => onTabChange('reviews')}
            className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
              tab === 'reviews'
                ? 'bg-yellow-500/25 text-yellow-200'
                : 'text-white/40 hover:text-white/70'
            }`}
            data-testid="feed-tab-reviews"
          >
            <Star className="h-3.5 w-3.5 inline mr-1" />
            Reviews {reviews.length > 0 && <span className="ml-1 text-[10px] opacity-70">({reviews.length})</span>}
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {tab === 'discussion' && (
            <div className="space-y-3" data-testid="feed-discussion-list">
              {chatChrono.length === 0 ? (
                <div className="text-center py-10 text-white/30 text-sm">
                  No discussion yet on this book.
                  <br />
                  <button
                    onClick={() => handleReply('chat')}
                    className="mt-3 inline-flex items-center text-cyan-400 text-xs hover:text-cyan-300"
                  >
                    <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open public chat
                  </button>
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto space-y-2 pr-1 -mr-1">
                  {chatChrono.map((m) => (
                    <div
                      key={m.id || `${m.user_name}-${m.created_at}`}
                      className="group flex items-start gap-2 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 p-3 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 text-xs">
                          <span className="font-medium text-white">{m.user_name}</span>
                          <span className="text-white/30">{formatRelative(m.created_at)}</span>
                        </div>
                        <p className="text-sm text-white/80 mt-0.5 break-words">{m.content}</p>
                      </div>
                      <button
                        onClick={() => handleReply('chat')}
                        title="Reply in the public chat"
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-cyan-400 hover:text-cyan-300 p-1 rounded"
                        data-testid={`feed-reply-chat-${m.id}`}
                      >
                        <Reply className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-white/30 text-center pt-1">
                Read-only preview · click the reply icon to engage in the public chat
              </p>
            </div>
          )}

          {tab === 'reviews' && (
            <div className="space-y-3" data-testid="feed-reviews-list">
              {reviews.length === 0 ? (
                <div className="text-center py-10 text-white/30 text-sm">
                  No reviews yet.
                  <br />
                  <button
                    onClick={() => handleReply('reviews')}
                    className="mt-3 inline-flex items-center text-yellow-400 text-xs hover:text-yellow-300"
                  >
                    <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open public reviews
                  </button>
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto space-y-2 pr-1 -mr-1">
                  {reviews.map((r) => (
                    <div
                      key={r.id || `${r.user_name}-${r.timestamp}`}
                      className="group flex items-start gap-3 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 p-3 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-xs flex-wrap">
                          <span className="font-medium text-white">{r.user_name || 'Anonymous'}</span>
                          <span className="text-yellow-400 tracking-tight">
                            {'★'.repeat(r.rating || 0)}<span className="text-white/15">{'★'.repeat(5 - (r.rating || 0))}</span>
                          </span>
                          <span className="text-white/30">{formatRelative(r.timestamp)}</span>
                          {(r.helpful_count || 0) > 0 && (
                            <span className="text-white/40">· {r.helpful_count} helpful</span>
                          )}
                        </div>
                        {r.comment && (
                          <p className="text-sm text-white/80 mt-1 line-clamp-4">{r.comment}</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleReply('reviews')}
                        title="Open public reviews"
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-yellow-400 hover:text-yellow-300 p-1 rounded"
                        data-testid={`feed-open-review-${r.id}`}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-white/30 text-center pt-1">
                Read-only preview · click the link icon to view in the public reviews section
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const StatCard = ({ label, value, Icon, testId }) => (
  <div className={card} data-testid={testId}>
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs text-white/40">{label}</span>
      {Icon && <Icon className="h-4 w-4 text-white/40" />}
    </div>
    <p className="text-2xl font-semibold text-white">{value}</p>
  </div>
);

const DateRangePicker = ({ label, value, min, max, onChange, testId }) => {
  // Parse the ISO string into a Date for the calendar; keep the bound
  // strings around for the onSelect comparison.
  const date = value ? parse(value, 'yyyy-MM-dd', new Date()) : undefined;
  const minDate = min ? parse(min, 'yyyy-MM-dd', new Date()) : undefined;
  const maxDate = max ? parse(max, 'yyyy-MM-dd', new Date()) : undefined;

  return (
    <div className="flex items-center gap-2">
      <span className="text-white/50">{label}</span>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              'h-8 px-3 bg-white/5 border-white/15 text-white hover:bg-white/10 hover:text-white text-sm',
              !value && 'text-white/40'
            )}
            data-testid={testId}
          >
            <CalendarIcon className="mr-1.5 h-3.5 w-3.5" />
            {value && isValid(date) ? format(date, 'MMM d, yyyy') : 'Pick date'}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0 bg-zinc-900 border-white/15"
          align="start"
          data-testid={`${testId}-popover`}
        >
          <Calendar
            mode="single"
            selected={date}
            onSelect={(d) => {
              if (d && isValid(d)) onChange(format(d, 'yyyy-MM-dd'));
            }}
            disabled={(d) => {
              if (minDate && d < minDate) return true;
              if (maxDate && d > maxDate) return true;
              return false;
            }}
            initialFocus
            className="text-white"
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

const LoadingSpinner = () => (
  <div className="flex items-center justify-center py-12">
    <div className="w-6 h-6 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

const CalendarHeatMap = ({ data, from, to }) => {
  const lookup = {};
  let maxViews = 1;
  (data || []).forEach((d) => {
    lookup[d.stat_date] = d.view_count;
    if (d.view_count > maxViews) maxViews = d.view_count;
  });

  const dates = [];
  const cur = new Date(from);
  const end = new Date(to);
  while (cur <= end) {
    dates.push(cur.toISOString().split('T')[0]);
    cur.setDate(cur.getDate() + 1);
  }

  const intensity = (count) => {
    if (!count) return 'bg-white/5';
    const ratio = count / maxViews;
    if (ratio < 0.25) return 'bg-red-900/40';
    if (ratio < 0.50) return 'bg-red-700/50';
    if (ratio < 0.75) return 'bg-red-500/60';
    return 'bg-red-400/80';
  };

  return (
    <div>
      <div className="flex flex-wrap gap-1">
        {dates.map((date) => {
          const count = lookup[date] || 0;
          return (
            <div
              key={date}
              title={`${date}: ${count} view${count === 1 ? '' : 's'}`}
              className={`w-3.5 h-3.5 rounded-sm cursor-default transition-opacity ${intensity(count)}`}
            />
          );
        })}
      </div>
      <div className="flex items-center gap-2 mt-3 text-xs text-white/30">
        <span>Less</span>
        <div className="w-3 h-3 rounded-sm bg-white/5" />
        <div className="w-3 h-3 rounded-sm bg-red-900/40" />
        <div className="w-3 h-3 rounded-sm bg-red-700/50" />
        <div className="w-3 h-3 rounded-sm bg-red-500/60" />
        <div className="w-3 h-3 rounded-sm bg-red-400/80" />
        <span>More</span>
      </div>
    </div>
  );
};

const SimpleLineChart = ({ data, xKey, yKey, color = '#ef4444', maxY = 100, unit = '' }) => {
  if (!data || data.length === 0) {
    return <p className="text-white/30 text-sm">No data in selected range yet.</p>;
  }
  const W = 600;
  const H = 140;
  const PAD = 32;
  const vals = data.map((d) => parseFloat(d[yKey]) || 0);
  const max = Math.max(maxY, ...vals);
  const xStep = (W - PAD * 2) / Math.max(data.length - 1, 1);

  const pts = data.map((d, i) => ({
    x: PAD + i * xStep,
    y: H - PAD - ((parseFloat(d[yKey]) || 0) / max) * (H - PAD * 2),
    label: d[xKey],
    val: parseFloat(d[yKey]) || 0,
  }));
  const pathD = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
      {[0, 0.25, 0.5, 0.75, 1].map((t) => {
        const y = PAD + t * (H - PAD * 2);
        return <line key={t} x1={PAD} y1={y} x2={W - PAD} y2={y} stroke="white" strokeOpacity="0.06" strokeWidth="1" />;
      })}
      <path d={pathD} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {pts.map((p) => (
        <g key={p.label}>
          <circle cx={p.x} cy={p.y} r="3" fill={color} />
          <title>{p.label}: {p.val}{unit}</title>
        </g>
      ))}
      <text x={PAD - 4} y={PAD + 4} textAnchor="end" fill="white" fillOpacity="0.3" fontSize="10">{max}{unit}</text>
      <text x={PAD - 4} y={H - PAD + 4} textAnchor="end" fill="white" fillOpacity="0.3" fontSize="10">0{unit}</text>
    </svg>
  );
};

export default MyBooks;
